import axios from "axios";
import { Map } from "immutable";
import { Howl, Howler } from "howler";
import { logError } from "../util";
import isFinite from "lodash/isFinite";

const assetsRequest = axios.create({ responseType: "blob" });

let store = new Map();
let currentPlaying = null;

const generateSpeechRecognitionSprites = (source) => {
  if (!source) {
    return null;
  }
  return source.reduce(
    (acc, wordScore, index) => ({
      ...acc,
      [`sprite_${index}`]: [
        wordScore.audioStartTime * 10 - 0.3,
        (wordScore.audioEndTime - wordScore.audioStartTime) * 10 + 0.3,
      ],
    }),
    {}
  );
};

const downloadAudio = async ({ url, isRetry }) => {
  let audioData = null;
  try {
    const response = await assetsRequest.get(
      `${isRetry ? process.env.REACT_APP_FILES_FALLBACK_URL : process.env.REACT_APP_FILES_URL}/${url}`
    );

    audioData = await new Promise((resolve) => {
      let reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
        reader = null;
      };
      reader.onerror = () => {
        reader = null;
        throw new Error("error_creating_base64");
      };
      reader.readAsDataURL(response.data);
    });
  } catch (error) {
    if (error.response) {
      logError({ error: `${error.response.status}: ${error.response.statusText}`, flow: "DOWNLOAD_AUDIO_DATA" });
    } else if (error.request) {
      logError({ error: `without_response`, flow: "DOWNLOAD_AUDIO_DATA" });
    } else {
      logError({ error: error.message, flow: "DOWNLOAD_AUDIO_DATA" });
    }
    return null;
  }

  if (audioData && audioData.indexOf("data:application/octet-stream") > -1) {
    const extension = url.split(".").pop();
    audioData = audioData.replace(
      "data:application/octet-stream",
      { mp3: "data:audio/mpeg", ogg: "audio/ogg", wav: "audio/x-wav", webm: "data:audio/webm" }[extension] ||
        "data:audio/mpeg"
    );
  }

  return audioData;
};

export const insertSpeechRecognitionAudio = async ({ data, isDeletable, spriteWords }) => {
  store = store.set(data, {
    data,
    isDeletable,
    spriteWords,
    audioSprites: generateSpeechRecognitionSprites(spriteWords),
  });
};

export const insertAudio = async ({ url, isDeletable, spriteWords }) => {
  const audioData = await downloadAudio({ url, isRetry: false });

  store = store.set(url, {
    data: audioData,
    isDeletable,
    spriteWords,
    audioSprites: generateSpeechRecognitionSprites(spriteWords),
  });

  if (!audioData) {
    setTimeout(async () => {
      const audioData = await downloadAudio({ url, isRetry: true });

      store = store.set(url, {
        data: audioData,
        isDeletable,
        spriteWords,
        audioSprites: generateSpeechRecognitionSprites(spriteWords),
      });
    }, 250);
  }
};

export const clearAudios = () => {
  store = store.filter((register) => !register.isDeletable);
};

export const getAudio = ({ url }) => {
  const register = store.get(url);
  if (!register) {
    throw new Error("audio_register_not_found");
  }
  if (!register.data) {
    throw new Error("audio_data_not_available");
  }

  return register.data;
};

export const getAudioInsertConfigs = ({ url }) => {
  const register = store.get(url);
  if (!register) {
    throw new Error("audio_register_not_found");
  }
  return register;
};

export const playAudio = async ({ url, rate, sprite, resolveOnPlay }) => {
  let audioData = null;
  let attempts = 0;
  let error = null;

  while (!audioData && attempts < 2) {
    try {
      audioData = getAudio({ url });
    } catch (e) {
      error = e;
      const { data, ...configs } = getAudioInsertConfigs({ url });

      await insertAudio({
        url,
        ...configs,
      });
      attempts++;
    }
  }

  if (!audioData) {
    throw error;
  }

  const { audioSprites } = getAudioInsertConfigs({ url });

  const sound = await new Promise((resolve, reject) => {
    const howlerInstance = new Howl({
      src: [audioData],
      autoplay: false,
      loop: false,
      html5: true,
      sprite: !!sprite ? audioSprites : null,
      onload: () => resolve(howlerInstance),
      onloaderror: (id, error) => {
        reject(`error_load_audio_${error}`);
      },
    });
  });

  let playTimeout = null;
  let suspendTimeout = null;
  let onEndTimeout = null;
  let soundId = null;
  return new Promise((resolve, reject) => {
    try {
      currentPlaying = sound;
      currentPlaying.once("end", () => {
        if (suspendTimeout) {
          clearTimeout(suspendTimeout);
          suspendTimeout = null;
        }
        if (playTimeout) {
          clearTimeout(playTimeout);
          playTimeout = null;
        }
        if (onEndTimeout) {
          clearTimeout(onEndTimeout);
          onEndTimeout = null;
        }
        if (currentPlaying) {
          currentPlaying.unload();
          currentPlaying = null;
        }
        soundId = null;
        resolve();
      });
      currentPlaying.once("playerror", (id, error) => {
        if (suspendTimeout) {
          clearTimeout(suspendTimeout);
          suspendTimeout = null;
        }
        if (playTimeout) {
          clearTimeout(playTimeout);
          playTimeout = null;
        }
        if (onEndTimeout) {
          clearTimeout(onEndTimeout);
          onEndTimeout = null;
        }
        if (currentPlaying) {
          currentPlaying.unload();
          currentPlaying = null;
        }
        soundId = null;
        reject(error);
      });
      currentPlaying.once("play", () => {
        let onEndTimeoutMilliseconds = 3000;
        if (soundId && currentPlaying && currentPlaying.duration(soundId)) {
          //audio duration in seconds * 1000 divided by the rate plus additional 500 milliseconds
          onEndTimeoutMilliseconds = (currentPlaying.duration(soundId) * 1000) / (rate || 1) + 500;
        }
        if (isFinite(onEndTimeoutMilliseconds)) {
          onEndTimeout = setTimeout(() => {
            if (suspendTimeout) {
              clearTimeout(suspendTimeout);
              suspendTimeout = null;
            }
            if (playTimeout) {
              clearTimeout(playTimeout);
              playTimeout = null;
            }
            if (onEndTimeout) {
              clearTimeout(onEndTimeout);
              onEndTimeout = null;
            }
            if (currentPlaying) {
              currentPlaying.unload();
              currentPlaying = null;
            }
            soundId = null;
            resolve();
          }, onEndTimeoutMilliseconds);
        }
        if (suspendTimeout) {
          clearTimeout(suspendTimeout);
          suspendTimeout = null;
        }
        if (playTimeout) {
          clearTimeout(playTimeout);
          playTimeout = null;
        }
        if (resolveOnPlay) {
          if (onEndTimeout) {
            clearTimeout(onEndTimeout);
          }
          soundId = null;
          resolve(currentPlaying);
        }
      });
      currentPlaying.once("stop", () => {
        if (suspendTimeout) {
          clearTimeout(suspendTimeout);
          suspendTimeout = null;
        }
        if (playTimeout) {
          clearTimeout(playTimeout);
          playTimeout = null;
        }
        if (onEndTimeout) {
          clearTimeout(onEndTimeout);
          onEndTimeout = null;
        }
      });
      if (!!sprite) {
        soundId = currentPlaying.play(sprite);
      } else {
        currentPlaying.rate(rate || 1);
        suspendTimeout = setTimeout(() => {
          if (Howler.state === "suspended") {
            if (currentPlaying) {
              currentPlaying.unload();
              currentPlaying = null;
            }
            reject("required-user-click");
          }
        }, 3000);
        playTimeout = setTimeout(() => {
          if (currentPlaying) {
            currentPlaying.unload();
            currentPlaying = null;
          }
          reject("not-started");
        }, 15000);
        soundId = currentPlaying.play();
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const stopAudio = () => {
  if (currentPlaying) {
    if (currentPlaying.playing()) {
      currentPlaying.stop();
    }
    currentPlaying = null;
  }
};
